import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    emailForgotPassword,
    emailResetPassword,
    emailVerifyResetCode,
    getAuthError,
    getCodeExpiresAt,
    getPendingEmail,
    getResetToken,
    getUserLoadingState,
    setAuthFlow
} from '../../../store/slices/authSlice';
import ResetPasswordCodeStep from './ResetPasswordCodeStep';
import ResetPasswordNewPasswordStep from './ResetPasswordNewPasswordStep';

const CODE_LENGTH = 6;

// HORO-10509: this used to be one screen (OTP box + a single password
// field, see the git history on this file for the long version of why),
// now split into two client-side "screens" — ResetPasswordCodeStep then
// ResetPasswordNewPasswordStep — behind local `screen` state here, not a
// second authSlice `authFlow` value. The backend now has a real
// POST /auth/verify-reset-code (media-ai-veda-backend
// EmailAuthService.verifyResetCode) that checks the 6-digit code on its
// own and hands back a short-lived `resetToken`; POST /auth/reset-password
// takes that token instead of the raw code, so a wrong/expired code
// rejects right here on the code screen, before ever advancing. "Back"
// from the password screen still returns to this local code state (not a
// redux flow change) — but the code it already verified there is spent
// server-side the moment that happens (see verifyResetCode's comment), so
// going back clears it rather than leaving a stale, already-consumed code
// sitting in the boxes.
const ResetPasswordStep = () => {
    const dispatch = useDispatch();
    const email = useSelector(getPendingEmail);
    const codeExpiresAt = useSelector(getCodeExpiresAt);
    const resetToken = useSelector(getResetToken);
    const loading = useSelector(getUserLoadingState);
    const error = useSelector(getAuthError);
    const [code, setCode] = useState('');
    const [screen, setScreen] = useState('code');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [remainingSec, setRemainingSec] = useState(null);
    const isCodeComplete = code.length === CODE_LENGTH;
    const isCodeExpired = remainingSec === 0;

    // Ticks every second off the absolute `codeExpiresAt` timestamp
    // (rather than a plain counter) so it stays correct across re-
    // renders and reflects a fresh expiry immediately when `onResend`
    // dispatches a new code, or when `onContinue` below verifies one —
    // both just change `codeExpiresAt` and this effect re-runs.
    useEffect(() => {
        if (!codeExpiresAt) {
            setRemainingSec(null);
            return undefined;
        }

        const tick = () => {
            setRemainingSec(Math.max(0, Math.round((codeExpiresAt - Date.now()) / 1000)));
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [codeExpiresAt]);

    const onResend = () => {
        if (resendCooldown > 0) {
            return;
        }
        dispatch(emailForgotPassword(email));
        // The forgot-password route has no per-call retryAfter of its own
        // (see authSlice's emailForgotPassword comment) — this is just a
        // fixed client-side cooldown against accidental double-taps, not
        // a read of a server-provided value like VerifyCodeStep's resend.
        setResendCooldown(60);
        const timer = setInterval(() => {
            setResendCooldown((current) => {
                if (current <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return current - 1;
            });
        }, 1000);
    };

    const onContinue = async () => {
        if (loading || !isCodeComplete || isCodeExpired) {
            return;
        }
        try {
            await dispatch(emailVerifyResetCode({ email, code })).unwrap();
            setScreen('password');
        } catch {
            // Rejected case already populates authSlice's `error`, shown
            // on ResetPasswordCodeStep as a normal form error — nothing
            // else to do here, and definitely don't advance the screen.
        }
    };

    const onBackToCodeStep = () => {
        // The code that got us here is already spent (verifyResetCode
        // deletes it server-side on success) — clearing it rather than
        // leaving it sitting in the boxes as if it were still good makes
        // that honest: re-entering the same digits and hitting Continue
        // again would just fail with "please request a new code".
        setCode('');
        setScreen('code');
    };

    const onSubmitPassword = ({ password }) => {
        if (loading || isCodeExpired) {
            return;
        }
        dispatch(emailResetPassword({ email, resetToken, password }));
    };

    if (screen === 'password') {
        return (
            <ResetPasswordNewPasswordStep
                loading={loading}
                error={error}
                isCodeExpired={isCodeExpired}
                onBack={onBackToCodeStep}
                onSubmit={onSubmitPassword}
            />
        );
    }

    return (
        <ResetPasswordCodeStep
            email={email}
            code={code}
            setCode={setCode}
            loading={loading}
            error={error}
            isCodeComplete={isCodeComplete}
            isCodeExpired={isCodeExpired}
            remainingSec={remainingSec}
            resendCooldown={resendCooldown}
            onResend={onResend}
            onContinue={onContinue}
            onBack={() => dispatch(setAuthFlow('forgot-password'))}
        />
    );
};

export default ResetPasswordStep;
